import { marked } from 'marked';

export const NEXT_STEPS_TITLES = Object.freeze([
  'NEXT STEPS EXECUTIVE SUMMARY', 'STRENGTHS LEVERAGE STRATEGY',
  'BEHAVIORAL MICRO-EXPERIMENTS', 'WHAT TO AVOID: EXPLORATION BOUNDARIES',
  'THERAPIST IMPLEMENTATION GUIDANCE', 'CLINICAL RELEVANCE NOTE: NOT A DIAGNOSIS',
  'MANDATORY DISCLAIMER',
]);
const legacyTitles = ['WHAT TO AVOID / BOUNDARIES', 'CONVERSATIONAL / FOLLOW-UP GUIDANCE', 'CULTURAL / CONTEXTUAL CONSIDERATIONS'];
export const nextStepsHeadingIdentity = (value) => {
  const doc = new DOMParser().parseFromString(String(value || ''), 'text/html');
  let text = doc.body.textContent || '';
  for (let i = 0; i < 3; i += 1) text = text.trim().replace(/[.:;!?]+$/, '').replace(/^#{1,6}\s*/, '').replace(/^[*_`]+|[*_`]+$/g, '').replace(/^\d+[.)]\s*/, '').trim();
  return text.normalize('NFKC').replace(/[\u2010-\u2015\u2212]/g, '-').replace(/[.:;!?]+$/, '').replace(/\s+/g, ' ').trim().toUpperCase();
};
const titles = [...NEXT_STEPS_TITLES, ...legacyTitles];
const identities = new Map(titles.map((title, index) => [title, index]));
const slotFor = (value) => identities.get(nextStepsHeadingIdentity(value));
const escape = (value) => value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// One source-to-section path for screen, export and speech. Only whole heading
// nodes/lines identify sections; title phrases inside clinical prose do not.
export const analyzeNextStepsSections = (raw) => {
  if (!raw || typeof DOMParser === 'undefined') return { sections: [], diagnostic: null };
  let source = String(raw).trim().replace(/^```(?:html|markdown|md)?\s*\n/i, '').replace(/\n?```\s*$/, '');
  source = source.split(/\r?\n/).map((line) => {
    const slot = slotFor(line);
    const ordinal = line.match(/^\s*(\d+)[.)]\s+/);
    // Only the canonical ordinal opens a top-level section. A different
    // ordinal with the same wording is an internal heading in the current
    // body and must remain in its original position.
    if (slot !== undefined && ordinal && Number(ordinal[1]) !== slot + 1) {
      return `<h3 class="next-steps-inline-heading">${escape(line.trim())}</h3>`;
    }
    // Native HTML is parsed as HTML so inline body text cannot be truncated.
    return slot !== undefined && !/<\/?[a-z][^>]*>/i.test(line) ? `\n<h2>${escape(titles[slot])}</h2>\n` : line;
  }).join('\n');
  const doc = new DOMParser().parseFromString(marked.parse(source), 'text/html');
  const blocks = [];
  let current = null;
  let preamble = '';
  const append = (html) => { if (current) current.bodyHtml += html; else preamble += html; };
  const start = (title, slot, open = false) => {
    current = { title, slot, bodyHtml: '', defaultOpen: open };
    blocks.push(current);
  };
  const walk = (node) => {
    if (node.nodeType === 3) {
      const text = node.nodeValue || '';
      append(text.trim() ? marked.parse(escape(text)) : text);
      return;
    }
    if (node.nodeType !== 1) return;
    const tag = node.tagName.toLowerCase();
    if (tag === 'details') {
      const summary = [...node.children].find(child => child.tagName === 'SUMMARY');
      if (summary) {
        start(summary.textContent.trim(), slotFor(summary.textContent), node.hasAttribute('open'));
        [...node.childNodes].filter(child => child !== summary).forEach(walk);
        return;
      }
    }
    const slot = slotFor(node.textContent);
    if (slot !== undefined && !node.classList.contains('next-steps-inline-heading') && /^(h[1-6]|p|strong|b|div)$/.test(tag)) {
      start(titles[slot], slot); return;
    }
    if (/^(div|section|article|main)$/.test(tag)) { [...node.childNodes].forEach(walk); return; }
    append(node.outerHTML);
  };
  [...doc.body.childNodes].forEach(walk);
  // Reports with no recognized Next Steps identity retain the established
  // generic renderer, including its historical keys and expansion defaults.
  const canonicalBlocks = blocks.filter(block => block.slot < NEXT_STEPS_TITLES.length);
  const canonicalSlots = [...new Set(canonicalBlocks.map(block => block.slot))].sort((a, b) => a - b);
  const signatureFor = (html) => {
    const body = new DOMParser().parseFromString(html.replace(/<\/(?:p|li|div|h[1-6])>|<br\s*\/?>/gi, '$& '), 'text/html').body;
    // Compare whole bodies only; preserve case, punctuation and link targets.
    return JSON.stringify([(body.textContent || '').replace(/\s+/g, ' ').trim(),
      [...body.querySelectorAll('[href],[src]')].map(node => [node.getAttribute('href'), node.getAttribute('src'), node.getAttribute('alt')])]);
  };
  const duplicateSlots = canonicalSlots.filter(slot => canonicalBlocks.filter(block => block.slot === slot).length > 1);
  const hasDetails = /<details\b/i.test(source);
  const diagnostic = {
    parser_version: 'next-steps-v2',
    parser_path: hasDetails ? 'html-details' : 'canonical-heading-walk',
    selected_path: blocks.some(block => block.slot !== undefined) ? 'structured' : 'fallback',
    raw_heading_count: blocks.length,
    canonical_heading_identities: canonicalSlots.map(slot => `next_steps_${slot + 1}`),
    duplicate_identities: duplicateSlots.map(slot => `next_steps_${slot + 1}`),
    duplicate_bodies: duplicateSlots.map(slot => {
      const bodies = canonicalBlocks.filter(block => block.slot === slot).map(block => block.bodyHtml).filter(body => body.replace(/<[^>]*>/g, '').trim());
      return { identity: `next_steps_${slot + 1}`, identical: new Set(bodies.map(signatureFor)).size <= 1 };
    }),
    // This records evidence, not an unsupported attribution to the provider.
    duplicate_origin: duplicateSlots.length ? 'source-heading-boundaries; generation-origin-unconfirmed' : null,
    anomalous: canonicalSlots.length !== 7 || duplicateSlots.length > 0,
  };
  if (!blocks.some(block => block.slot !== undefined)) return { sections: [], diagnostic };
  blocks[0].bodyHtml = preamble + blocks[0].bodyHtml;
  const result = [];
  const bySlot = new Map();
  const signatures = new Map();
  blocks.forEach((block) => {
    const existing = block.slot === undefined ? null : bySlot.get(block.slot);
    const signature = signatureFor(block.bodyHtml);
    if (existing) {
      // Deduplicate complete identical section bodies only. Differing bodies
      // remain intact, including repeated citations, questions and disclaimers.
      if (block.bodyHtml.trim() && !signatures.get(block.slot).has(signature)) existing.bodyHtml += `\n${block.bodyHtml}`;
      signatures.get(block.slot).add(signature);
      return;
    }
    const section = { ...block, title: block.slot === undefined ? block.title : titles[block.slot] };
    result.push(section);
    if (block.slot !== undefined) { bySlot.set(block.slot, section); signatures.set(block.slot, new Set([signature])); }
  });
  result.sort((a, b) => (a.slot ?? titles.length) - (b.slot ?? titles.length));
  const sections = result.map((section, index) => ({ ...section,
    id: section.slot === undefined ? `next_steps_unknown_${index}_0` : `next_steps_${section.slot + 1}_0`,
    summaryHtml: escape(section.title), defaultOpen: section.defaultOpen || (index === 0 && !/<details\b/i.test(source)),
  }));
  return { sections, diagnostic };
};

export const parseNextStepsSections = raw => analyzeNextStepsSections(raw).sections;

export const resolveNextStepsSections = (raw, fallback) => {
  const primary = analyzeNextStepsSections(raw);
  if (primary.sections.length) return primary;
  const legacy = fallback();
  const normalized = analyzeNextStepsSections(legacy.map(section =>
    `<details${section.defaultOpen ? ' open' : ''}><summary>${escape(section.title)}</summary>${section.bodyHtml}</details>`).join('\n'));
  return {
    sections: normalized.sections.length ? normalized.sections : legacy,
    diagnostic: { ...(normalized.diagnostic || primary.diagnostic), selected_path: 'fallback', parser_path: 'legacy-fallback-normalized' },
  };
};

const emitted = new Set();
export const logNextStepsDiagnostic = async (raw, diagnostic, context = {}) => {
  if (!diagnostic?.anomalous || !globalThis.crypto?.subtle) return;
  const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(String(raw || '')));
  const contentHash = [...new Uint8Array(digest)].map(value => value.toString(16).padStart(2, '0')).join('');
  const evidence = {
    event: 'next_steps_section_anomaly', ...diagnostic,
    session_id: context.sessionId || null, report_id: 'next_steps',
    report_version: context.reportVersion || null,
    generation_request_id: context.generationRequestId || null, content_hash: contentHash,
  };
  const key = JSON.stringify(evidence);
  if (emitted.has(key)) return;
  if (emitted.size >= 100) emitted.delete(emitted.values().next().value);
  emitted.add(key);
  console.warn('[NextSteps]', evidence);
};

export const nextStepsDisplayHtml = (raw) => {
  const sections = parseNextStepsSections(raw);
  return sections.length ? sections.map(section => `<details><summary>${escape(section.title)}</summary>${section.bodyHtml}</details>`).join('\n') : raw;
};
export const nextStepsText = (raw) => {
  const html = nextStepsDisplayHtml(raw);
  const doc = new DOMParser().parseFromString(String(html).replace(/<br\s*\/?>|<\/(?:p|li|h[1-6]|summary|details)>/gi, '\n'), 'text/html');
  return (doc.body.textContent || '').trim();
};
